<script setup lang="ts">
import { reactive, ref, watch, onMounted, onBeforeUnmount, Ref, inject, nextTick } from 'vue'
import { useRouter } from 'vue-router'

// shadcn
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'

// lucide
import {
	ChevronDown, PanelLeftClose, PanelLeftOpen, Home, Plus,
	Paintbrush, UserPlus, FileOutput, Search, X, Loader2,
	Users, UserRound, FolderOpen, Shield, ShieldCheck, UserCog
} from 'lucide-vue-next'

// local
import permissions from '@/services/permissions'
import TreeView from '@/components/TreeView.vue'
import UserTable from '@/components/UserTable.vue'
import { columns, defaultColumnVisibility } from '@/components/usercolumns'
import { Filters, defaultFilters } from '@/types/Filters'
import { useGroups } from '@/services/Groups'
import { Member } from '@/types/Member'
import { getUsers } from '@/services/User'
import { useCookies } from '@vueuse/integrations/useCookies'
import CreateUser from '@/components/CreateUser.vue'
import { toast } from 'vue-sonner'

const router = useRouter()
const { checkPermission } = permissions()
const { getGroupTree, groupTree } = useGroups()
const cookies = useCookies(['groupMenu'])
const mmMode = inject<Ref<boolean>>('mmMode', ref(false))
const showCreateUser = ref(false)

const loading = ref(true)
const users = ref<Member[]>([])
const totalCount = ref(0)
const currentCount = ref(0)
const sortOrder = ref('created DESC')
const filters = reactive<Filters>({})
const showGroups = ref(false)
const isDragging = ref(false)
const selectedGroup = ref<number | null>(null)
const parentGroup = ref<number | null>(null)
const pageSize = 245

// mmMode: role-based filter tabs
const mmRoleFilter = ref('all')
const mmRoleFilters = [
	{ value: 'all', label: 'All', icon: Users },
	{ value: 'admin', label: 'Admin', icon: ShieldCheck },
	{ value: 'superadmin', label: 'Super Admin', icon: Shield },
	{ value: 'contributor', label: 'Contributor', icon: UserCog },
]

const vhostId = inject<Ref<string> | null>('vhostId', null)
const userInfo = inject<Ref<Member> | null>('userInfo', null)

// Column visibility — restore from cookie or use defaults
const savedColVis = cookies.get('newSparkUserColumns') as Record<string, boolean> | undefined
const columnVisibility = ref<Record<string, boolean>>(savedColVis || { ...defaultColumnVisibility })

const onColumnVisibilityChange = (newVis: Record<string, boolean>) => {
	columnVisibility.value = newVis
	cookies.set('newSparkUserColumns', newVis, { maxAge: 86400 * 90, path: '/' })
}

// Search
const searchOpen = ref(false)
const searchQuery = ref('')
const searchResults = ref<Member[]>([])
const searchLoading = ref(false)
let searchTimeout: ReturnType<typeof setTimeout> | null = null

const formatLargeNumber = (num: number): string => num.toLocaleString('en-US')

const fetchGroupTree = async (vhost: string) => {
	if (vhost) await getGroupTree(vhost)
}

const fetchUsers = async () => {
	const vid = vhostId?.value
	if (!vid) {
		console.warn('fetchUsers: vhostId not available yet')
		return
	}
	loading.value = true
	try {
		const result = await getUsers(Number(vid), 0, pageSize, filters.query || undefined, sortOrder.value)
		users.value = result.users
		totalCount.value = result.totalCount
		currentCount.value = result.users.length
	} catch (error) {
		console.error(error)
	} finally {
		loading.value = false
	}
}

const setGroupFilter = (id: any) => {
	if (id && id > 0) {
		selectedGroup.value = id
		filters.groupid = id
	} else {
		selectedGroup.value = 0
		delete filters.groupid
	}
}

const toggleGroupList = () => {
	showGroups.value = !showGroups.value
	cookies.set('groupMenu', showGroups.value, { maxAge: 86400 * 30, path: '/' })
}

const onSearchInput = (e: Event) => {
	const query = (e.target as HTMLInputElement).value
	searchQuery.value = query
	if (searchTimeout) clearTimeout(searchTimeout)
	if (!query || query.length < 2) {
		searchResults.value = []
		return
	}
	searchTimeout = setTimeout(async () => {
		if (!vhostId?.value) return
		searchLoading.value = true
		try {
			const result = await getUsers(Number(vhostId.value), 0, 15, query)
			searchResults.value = result.users
		} catch (e) {
			console.error('Search error:', e)
		} finally {
			searchLoading.value = false
		}
	}, 300)
}

const clearSearch = () => {
	searchQuery.value = ''
	searchResults.value = []
	searchOpen.value = false
}

const selectSearchResult = (member: Member) => {
	clearSearch()
	router.push('/member/' + member.id)
}

const props = defineProps({
	vhost: { type: String, default: null },
})

watch(sortOrder, () => {
	if (vhostId?.value) fetchUsers()
})

// Track whether initial fetch has been done to avoid duplicate calls
const initialFetchDone = ref(false)

const doInitialFetch = async (vhost: string) => {
	if (initialFetchDone.value) return
	initialFetchDone.value = true
	await fetchGroupTree(vhost)
	await fetchUsers()
}

// Watch for vhostId to become available (it's set async in App.vue)
watch(() => vhostId?.value, async (newVhost, oldVhost) => {
	if (newVhost && newVhost !== oldVhost) {
		initialFetchDone.value = false
		await doInitialFetch(newVhost)
	}
})

onMounted(async () => {
	const savedGroupMenu = cookies.get('groupMenu')
	// In mmMode default sidebar open; otherwise respect cookie or default closed
	if (savedGroupMenu !== undefined && savedGroupMenu !== null) {
		showGroups.value = savedGroupMenu
	} else {
		showGroups.value = mmMode.value ? true : false
	}
	if (vhostId?.value) {
		await doInitialFetch(vhostId.value)
	}
})
</script>

<template>
	<div v-if="userInfo && vhostId && userInfo.permissions" class="flex border-t text-secondary-foreground h-screen">

		<!-- Sidebar — Desktop -->
		<div v-if="showGroups || isDragging" class="hidden md:flex border-r w-[240px] shrink-0 flex-col bg-background">
			<div class="flex items-center justify-between px-3 py-2.5 border-b">
				<span class="text-sm font-medium">Projects & Folders</span>
				<button @click="toggleGroupList()" class="p-1 rounded hover:bg-muted text-muted-foreground">
					<PanelLeftClose class="h-4 w-4" />
				</button>
			</div>
			<div class="px-3 py-2">
				<Button size="sm" variant="outline" class="w-full gap-1.5 text-xs" @click="toast.info('Create new folder coming soon')">
					<Plus class="h-3.5 w-3.5" /> New Folder
				</Button>
			</div>
			<div class="flex-1 overflow-y-auto px-2 pb-4">
				<button class="flex items-center gap-2 w-full px-2 py-1.5 rounded text-sm hover:bg-muted transition-colors"
					:class="{ 'bg-muted font-medium': !selectedGroup || selectedGroup === 0 }"
					@click.prevent="setGroupFilter(0)">
					<Home class="h-3.5 w-3.5 text-muted-foreground" /> All Users
				</button>
				<div class="mt-1">
					<TreeView :groupTree="groupTree" :selectedGroup="selectedGroup || undefined" :parentGroup="parentGroup || undefined" @select-group="setGroupFilter" />
				</div>
			</div>
		</div>

		<!-- Mobile sidebar overlay -->
		<Sheet v-model:open="showGroups">
			<SheetContent side="left" class="w-[280px] p-0 md:hidden">
				<SheetHeader class="px-3 py-2.5 border-b">
					<SheetTitle class="text-sm font-medium">Projects & Folders</SheetTitle>
				</SheetHeader>
				<div class="px-3 py-2">
					<Button size="sm" variant="outline" class="w-full gap-1.5 text-xs">
						<Plus class="h-3.5 w-3.5" /> New Folder
					</Button>
				</div>
				<div class="flex-1 overflow-y-auto px-2 pb-4">
					<button class="flex items-center gap-2 w-full px-2 py-1.5 rounded text-sm hover:bg-muted transition-colors"
						:class="{ 'bg-muted font-medium': !selectedGroup || selectedGroup === 0 }"
						@click.prevent="setGroupFilter(0); showGroups = false">
						<Home class="h-3.5 w-3.5 text-muted-foreground" /> All Users
					</button>
					<div class="mt-1">
						<TreeView :groupTree="groupTree" :selectedGroup="selectedGroup || undefined" :parentGroup="parentGroup || undefined" @select-group="(id: any) => { setGroupFilter(id); showGroups = false; }" />
					</div>
				</div>
			</SheetContent>
		</Sheet>

		<!-- Collapsed sidebar toggle (desktop) -->
		<div v-if="!showGroups && !isDragging" class="hidden md:flex border-r shrink-0 flex-col items-center py-3 w-10">
			<button @click="toggleGroupList()" class="p-1.5 rounded hover:bg-muted text-muted-foreground" title="Open folders">
				<PanelLeftOpen class="h-4 w-4" />
			</button>
		</div>

		<!-- Main content -->
		<div class="flex-1 flex flex-col min-w-0 bg-card">

			<!-- Toolbar -->
			<div class="bg-background border-b sticky top-0 z-50">
				<!-- Mobile toolbar -->
				<div class="flex lg:hidden items-center justify-between px-2 py-2 gap-2">
					<div class="flex items-center gap-1.5">
						<button @click="showGroups = true" class="p-2 rounded-md hover:bg-muted text-muted-foreground">
							<FolderOpen class="h-4 w-4" />
						</button>
						<button @click="searchOpen = !searchOpen" class="p-2 rounded-md hover:bg-muted text-muted-foreground"
							:class="{ 'bg-primary/10 text-primary': searchOpen }">
							<Search class="h-4 w-4" />
						</button>
						<Button variant="outline" size="sm" class="gap-1 text-xs h-8 px-2" @click="showCreateUser = true">
							<UserPlus class="h-3.5 w-3.5" />
						</Button>
					</div>
					<div class="flex items-center gap-1.5">
						<span v-if="totalCount > 0" class="text-xs text-muted-foreground tabular-nums">
							{{ formatLargeNumber(totalCount) }}
						</span>
					</div>
				</div>
				<!-- Mobile search panel -->
				<div v-if="searchOpen" class="lg:hidden border-t relative">
					<div class="flex items-center gap-2 px-3 py-2">
						<Search class="h-4 w-4 text-muted-foreground flex-shrink-0" />
						<input
							type="text"
							:value="searchQuery"
							@input="onSearchInput"
							placeholder="Search users..."
							class="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/60"
							autofocus
						/>
						<Loader2 v-if="searchLoading" class="h-4 w-4 animate-spin text-muted-foreground flex-shrink-0" />
						<button v-if="searchQuery" @click="clearSearch" class="p-1 rounded-md hover:bg-muted text-muted-foreground">
							<X class="h-3.5 w-3.5" />
						</button>
					</div>
					<!-- Results dropdown -->
					<div v-if="searchResults.length > 0" class="absolute left-0 right-0 top-full bg-background border-t shadow-lg max-h-[60vh] overflow-y-auto z-50">
						<button v-for="result in searchResults" :key="result.id"
							class="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted active:bg-muted/80 transition-colors text-left border-b border-border/30 last:border-0"
							@click="selectSearchResult(result)">
							<div class="h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
								<UserRound class="h-4 w-4 text-muted-foreground" />
							</div>
							<div class="flex-1 min-w-0">
								<p class="text-sm font-medium truncate">{{ result.firstname }} {{ result.lastname }}</p>
								<p class="text-[11px] text-muted-foreground truncate">{{ result.email }}</p>
							</div>
						</button>
					</div>
					<div v-else-if="searchQuery.length >= 2 && !searchLoading && searchResults.length === 0" class="absolute left-0 right-0 top-full bg-background border-t shadow-lg z-50 px-4 py-6 text-center">
						<p class="text-sm text-muted-foreground">No users found</p>
					</div>
				</div>

				<!-- Desktop toolbar -->
				<div class="hidden lg:flex items-center justify-between px-4 py-2 gap-3">
					<div class="flex items-center gap-2">
						<!-- Search -->
						<div class="relative">
							<div class="flex items-center gap-2 border rounded-md px-3 py-1.5 bg-card w-64">
								<Search class="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
								<input
									type="text"
									:value="searchQuery"
									@input="onSearchInput"
									:placeholder="mmMode ? 'Search admins...' : 'Search users...'"
									class="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/60"
								/>
								<Loader2 v-if="searchLoading" class="h-3.5 w-3.5 animate-spin text-muted-foreground" />
								<button v-if="searchQuery" @click="clearSearch" class="text-muted-foreground hover:text-foreground">
									<X class="h-3.5 w-3.5" />
								</button>
							</div>
							<!-- Desktop search results -->
							<div v-if="searchResults.length > 0" class="absolute left-0 top-full mt-1 bg-background border rounded-lg shadow-lg max-h-[50vh] overflow-y-auto z-50 w-80">
								<button v-for="result in searchResults" :key="result.id"
									class="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted transition-colors text-left border-b border-border/30 last:border-0"
									@click="selectSearchResult(result)">
									<div class="h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
										<UserRound class="h-4 w-4 text-muted-foreground" />
									</div>
									<div class="flex-1 min-w-0">
										<p class="text-sm font-medium truncate">{{ result.firstname }} {{ result.lastname }}</p>
										<p class="text-[11px] text-muted-foreground truncate">{{ result.email }}</p>
									</div>
								</button>
							</div>
							<div v-else-if="searchQuery.length >= 2 && !searchLoading && searchResults.length === 0" class="absolute left-0 top-full mt-1 bg-background border rounded-lg shadow-lg z-50 w-80 px-4 py-4 text-center">
								<p class="text-sm text-muted-foreground">No users found</p>
							</div>
						</div>

						<!-- mmMode: Role filter tabs + Create User -->
						<template v-if="mmMode">
							<div class="flex items-center border rounded-md overflow-hidden">
								<button v-for="role in mmRoleFilters" :key="role.value"
									class="px-3 py-1.5 text-xs transition-colors flex items-center gap-1.5"
									:class="mmRoleFilter === role.value ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground'"
									@click="mmRoleFilter = role.value">
									<component :is="role.icon" class="h-3.5 w-3.5" />
									{{ role.label }}
								</button>
							</div>
							<Button variant="outline" size="sm" class="gap-1.5 text-xs" @click="showCreateUser = true">
								<UserPlus class="h-3.5 w-3.5" /> Create User
							</Button>
						</template>

						<!-- mediafactory: Actions / Create User / Export -->
						<template v-if="!mmMode">
							<DropdownMenu>
								<DropdownMenuTrigger as-child>
									<Button variant="outline" size="sm" class="gap-1.5 text-xs">
										<Paintbrush class="h-3.5 w-3.5" /> Actions <ChevronDown class="h-3 w-3" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent>
									<DropdownMenuItem class="text-xs gap-2" @click="toast.info('Select users from the table to add to a folder')">Add to Folder</DropdownMenuItem>
									<DropdownMenuItem class="text-xs gap-2" @click="toast.info('Select users to disable')">Disable</DropdownMenuItem>
									<DropdownMenuItem class="text-xs gap-2" @click="toast.info('Select users to enable')">Enable</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>

							<Button variant="outline" size="sm" class="gap-1.5 text-xs" @click="showCreateUser = true">
								<UserPlus class="h-3.5 w-3.5" /> Create User
							</Button>
							<Button variant="outline" size="sm" class="gap-1.5 text-xs" @click="toast.info('Export functionality coming soon')">
								<FileOutput class="h-3.5 w-3.5" /> Export Report
							</Button>
						</template>
					</div>

					<div class="flex items-center gap-3">
						<span v-if="currentCount > 0 && totalCount > 0" class="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
							{{ currentCount }} of {{ formatLargeNumber(totalCount) }}
						</span>

						<Select v-model="sortOrder">
							<SelectTrigger class="h-8 w-44 text-xs bg-card">
								<SelectValue placeholder="Sort order" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="created DESC">Newest first</SelectItem>
								<SelectItem value="created ASC">Oldest first</SelectItem>
								<SelectItem value="firstname ASC">First name A-Z</SelectItem>
								<SelectItem value="firstname DESC">First name Z-A</SelectItem>
								<SelectItem value="lastname ASC">Last name A-Z</SelectItem>
								<SelectItem value="lastname DESC">Last name Z-A</SelectItem>
								<SelectItem value="email ASC">Email A-Z</SelectItem>
								<SelectItem value="lastlogin DESC">Last login</SelectItem>
							</SelectContent>
						</Select>
					</div>
				</div>
			</div>

			<!-- User table -->
			<div class="flex-1 overflow-y-auto p-3 md:p-4">
				<!-- Loading state -->
				<div v-if="loading" class="space-y-3">
					<Skeleton class="h-10 w-full" />
					<Skeleton v-for="i in 8" :key="i" class="h-12 w-full" />
				</div>

				<!-- Empty state -->
				<div v-else-if="!loading && users.length === 0" class="flex flex-col items-center justify-center py-20 gap-4">
					<div class="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
						<Users class="h-8 w-8 text-muted-foreground" />
					</div>
					<div class="text-center">
						<p class="text-lg font-medium">No users found</p>
						<p class="text-sm text-muted-foreground mt-1">Try adjusting your filters or invite new users.</p>
					</div>
				</div>

				<!-- Table -->
				<div v-else class="bg-card">
					<UserTable :data="users" :columns="columns" :initialColumnVisibility="columnVisibility" @update:columnVisibility="onColumnVisibilityChange" />
				</div>
			</div>
		</div>
	</div>

	<!-- Create User Dialog -->
	<CreateUser v-model="showCreateUser" />
</template>
